import "./index.less";

import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Button, message, Input } from "antd";
import { useTranslate } from "@obsbot/remo-i18n";

import { useIsUnMounted, useSafePromise } from "../../../utils/hooks";
import { RequestErrorCode } from "@obsbot/remo-server-api";
import { errorTips } from "../../../utils/errorTip";
import { filterInputValueForNumber } from "@obsbot/remo-utils";

export interface LoginSendCodeProps {
    isAccountValidated: boolean;
    // BindingPhoneSendCodeResult for binding phone page
    sendVerificationCode: () => Promise<boolean>;
    // for rebinding phone
    onRebinding?: () => void;
    value?: string;
    onChange?: (value: string) => void;
    onFocus?: () => void;
}

export const LoginSendCode: React.FC<LoginSendCodeProps> = ({
    isAccountValidated,
    sendVerificationCode,
    onRebinding,
    value,
    onChange,
    ...restProps
}) => {
    const isUnMountRef = useIsUnMounted();
    const sp = useSafePromise();
    const t = useTranslate();

    const [countdown, setCountdown] = useState(0);
    const [sendingCode, setSendingCode] = useState(false);

    const sendCode = useCallback(async () => {
        if (isAccountValidated) {
            try {
                setSendingCode(true);
                const sent = await sp(sendVerificationCode());
                setSendingCode(false);

                if (sent) {
                    message.info(t("sent-verify-code-to-account"));
                    let count = 60;
                    setCountdown(count);
                    const timer = setInterval(() => {
                        if (isUnMountRef.current) {
                            clearInterval(timer);
                            return;
                        }
                        setCountdown(--count);
                        if (count === 0) {
                            clearInterval(timer);
                        }
                    }, 1000);
                } else {
                    message.error(t("login-page.send-verify-code-failed"));
                }
            } catch (error) {
                if (!isUnMountRef.current) {
                    setSendingCode(false);
                }

                // we say the phone is already binding when error message contains `RequestErrorCode.SMSAlreadyBinding`
                // and then we can enter rebinding page to rebind.
                if (error.message.indexOf(RequestErrorCode.SMSAlreadyBinding) > -1 && onRebinding) {
                    onRebinding();
                    return;
                }

                errorTips(error);
            }
        }
    }, [isAccountValidated, sp, sendVerificationCode, t, isUnMountRef, onRebinding]);

    // Workaround to prevent browser auto complete this field
    // however, it still cannot prevent auto complete panel from showing up
    const [readOnly, setReadOnly] = useState(true);
    useEffect(() => {
        const ticket = setTimeout(() => {
            setReadOnly(false);
        }, 1000);
        return () => clearTimeout(ticket);
    }, []);

    return (
        <Fragment>
            <div className="login-send-code-title">
                <span>{t("login-page.verify-code")}</span>
            </div>
            <div className="login-send-code-container">
                <Input
                    addonAfter={
                        <Button
                            className="login-send-code-btn"
                            disabled={countdown > 0 || !isAccountValidated}
                            loading={sendingCode}
                            type="text"
                            onClick={sendCode}
                        >
                            {countdown > 0
                                ? t("login-page.seconds-to-resend", { seconds: countdown })
                                : t("login-page.send-verify-code")}
                        </Button>
                    }
                    autoComplete="off"
                    className="login-send-code-input"
                    maxLength={6}
                    minLength={6}
                    placeholder={t("login-page.enter-code")}
                    readOnly={readOnly}
                    value={value}
                    onChange={e => {
                        onChange?.(filterInputValueForNumber(e.target.value));
                    }}
                    onKeyDown={e => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                            e.preventDefault();
                        }
                    }}
                    {...restProps}
                />
                {/* <Button
                    className="login-send-code-btn"
                    disabled={countdown > 0 || !isAccountValidated}
                    loading={sendingCode}
                    type="text"
                    onClick={sendCode}
                >
                    {countdown > 0
                        ? t("login-page.seconds-to-resend", { seconds: countdown })
                        : t("login-page.send-verify-code")}
                </Button> */}
            </div>
        </Fragment>
    );
};
